import type {
  User,
  AuthenticatedUser,
  PublicUser,
  UserPermissions,
  UserProfile,
  StripeSubscription,
} from 'iq-auth/types';
import {fallbackUserPermissions, UserGroup} from 'iq-auth/types';
import {API} from 'common/api';

export const buildPublicUser = (): PublicUser => ({
  loggedIn: false,
  hasToConfirmEmail: false,
  isAdmin: false,
  hasSubscription: false,
  permissions: fallbackUserPermissions,
  subscriptions: [],
});

const buildAuthenticatedUser = (
  userProfile: UserProfile,
  permissions: UserPermissions,
  subscriptions: StripeSubscription[],
): AuthenticatedUser => ({
  ...userProfile,
  permissions,
  subscriptions,
  loggedIn: true,
  hasSubscription:
    subscriptions.length > 0 ||
    ![UserGroup.Public, UserGroup.Free].includes(userProfile.user_group_id),
  hasToConfirmEmail: !userProfile.is_confirmed,
  isAdmin: userProfile.user_group_id === UserGroup.Admin,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseUserResponse = (data: any): User => {
  if (!data?.user) return buildPublicUser();
  return buildAuthenticatedUser(
    data.user as UserProfile,
    (data.permissions as UserPermissions) ?? fallbackUserPermissions,
    (data.subscriptions as StripeSubscription[]) ?? [],
  );
};

/**
 * Fetch user client-side (sends cookies automatically via `credentials: include`).
 * Call this from Client Components when no serverUser is available.
 */
export const getUser = async (): Promise<User> => {
  try {
    return parseUserResponse(await API.get('/v1/user'));
  } catch {
    return buildPublicUser();
  }
};
